import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Eraser, Loader2, PenLine } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API, BACKEND, eur, dateFr, dateTimeFr } from "@/lib/api";
import { appliquerLangue } from "@/i18n";
import StatutBadge from "@/components/StatutBadge";
import Logo from "@/components/Logo";
import LangueSwitcher from "@/components/LangueSwitcher";
import Modal from "@/components/Modal";

function SignaturePad({ onSign, effacerLabel }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  useEffect(() => {
    const canvas = ref.current;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = 180 * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#221F1A";
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = (e) => {
    drawing.current = true;
    const p = pos(e);
    const ctx = ref.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    hasInk.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onSign(hasInk.current ? ref.current.toDataURL("image/png") : "");
  };
  const clear = () => {
    const canvas = ref.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
    onSign("");
  };

  return (
    <div>
      <div className="border-2 border-dashed border-[#E6DFD1] rounded-xl bg-white overflow-hidden">
        <canvas ref={ref} style={{ width: "100%", height: 180, touchAction: "none", cursor: "crosshair" }}
                onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
                data-testid="canvas-signature" />
      </div>
      <button type="button" onClick={clear}
              className="mt-2 text-sm font-semibold text-[#8A5A2B] flex items-center gap-1"
              data-testid="btn-effacer-signature">
        <Eraser size={14} /> {effacerLabel}
      </button>
    </div>
  );
}

export default function PublicDevis() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [devis, setDevis] = useState(null);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [refused, setRefused] = useState(false);
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nom, setNom] = useState("");
  const [sigData, setSigData] = useState("");
  const [langueVisiteur, setLangueVisiteur] = useState(() => localStorage.getItem("mastoras_langue_public") || "");

  useEffect(() => {
    axios.get(`${API}/public/devis/${token}`)
      .then((r) => {
        setDevis(r.data);
        setAccepted(r.data.statut === "accepte");
        setRefused(r.data.statut === "refuse");
        appliquerLangue(langueVisiteur || r.data.entreprise?.langue || "fr");
      })
      .catch(() => setError(t("public_devis.introuvable")));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  function changerLangue(code) {
    setLangueVisiteur(code);
    localStorage.setItem("mastoras_langue_public", code);
  }

  async function refuser() {
    setBusy(true);
    try {
      await axios.post(`${API}/public/devis/${token}/refuser`);
      setRefused(true);
      setDevis({ ...devis, statut: "refuse" });
      setRefuseOpen(false);
    } catch {
      setError(t("public_devis.erreur_action"));
    } finally {
      setBusy(false);
    }
  }

  async function accepter() {
    setBusy(true);
    try {
      await axios.post(`${API}/public/devis/${token}/accepter`, { nom_signataire: nom, signature: sigData });
      setAccepted(true);
      setDevis({
        ...devis, statut: "accepte",
        signature: sigData ? { data: sigData, nom, date: new Date().toISOString() } : devis.signature,
      });
    } catch {
      setError(t("public_devis.erreur_accept"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#12100D] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Logo size={40} iconSize={20} logoUrl={devis?.entreprise?.logo_url ? `${BACKEND}${devis.entreprise.logo_url}` : ""} />
            <span className="font-serif font-bold text-xl text-white">Mastoras</span>
          </div>
          <LangueSwitcher sombre onChange={changerLangue} />
        </div>

        {error ? (
          <div className="bg-[#F7F4EE] rounded-2xl p-8 text-center" data-testid="public-devis-erreur">
            <p className="text-[#A32E20] font-semibold">{error}</p>
          </div>
        ) : !devis ? (
          <div className="text-stone-400 text-center py-20">{t("commun.chargement")}</div>
        ) : (
          <div className="bg-[#F7F4EE] rounded-2xl overflow-hidden" data-testid="public-devis">
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between gap-3 mb-1">
                <div>
                  <h1 className="font-serif font-bold text-2xl sm:text-3xl">{t("public_devis.devis")} {devis.numero}</h1>
                  <p className="text-stone-500 text-sm mt-1">
                    {devis.entreprise?.raison_sociale} · {dateFr(devis.cree_le)}
                  </p>
                </div>
                <StatutBadge statut={devis.statut} testid="badge-statut-public" />
              </div>
              {devis.chantier && (
                <p className="text-sm text-stone-600 mb-2">{t("public_devis.chantier")} {devis.chantier.titre}</p>
              )}
              <p className="text-sm text-stone-600">{t("public_devis.pour")} {devis.client?.nom}</p>
              {devis.objet && (
                <p className="mt-2 font-medium text-[#8A5A2B]" data-testid="objet-devis-public">{devis.objet}</p>
              )}

              <div className="mt-6 bg-white rounded-xl border border-[#E6DFD1] overflow-hidden">
                <div className="divide-y divide-[#E6DFD1]">
                  {devis.lignes.map((l, i) => (
                    <div key={i} className="flex justify-between gap-3 p-4 text-sm">
                      <div>
                        <div className="font-medium">{l.designation}</div>
                        <div className="text-stone-500 text-xs">{l.quantite}{l.unite ? ` ${l.unite}` : ""} × {eur(l.prix_unitaire)}</div>
                      </div>
                      <span className="font-serif font-semibold shrink-0">{eur(l.quantite * l.prix_unitaire)}</span>
                    </div>
                  ))}
                  {((devis.mode_mo === "horaire" && Number(devis.heures_mo) > 0)
                    || (devis.mode_mo !== "horaire" && Number(devis.montant_forfait_mo) > 0)) && (
                    <div className="flex justify-between gap-3 p-4 text-sm" data-testid="ligne-main-oeuvre">
                      <div>
                        <div className="font-medium">{t("devis_form.main_oeuvre")}</div>
                        <div className="text-stone-500 text-xs">{devis.mode_mo === "horaire"
                          ? `${devis.heures_mo} h × ${eur(devis.taux_horaire_mo)}/h`
                          : t("devis_form.mode_forfait")}</div>
                      </div>
                      <span className="font-serif font-semibold shrink-0">{eur(devis.mode_mo === "horaire"
                        ? (devis.heures_mo || 0) * (devis.taux_horaire_mo || 0)
                        : (devis.montant_forfait_mo || 0))}</span>
                    </div>
                  )}
                </div>
                <div className="bg-[#12100D] p-5">
                  <div className="flex justify-between text-stone-400 text-sm">
                    <span>{t("public_devis.total_ht")}</span><span className="font-serif">{eur(devis.total_ht)}</span>
                  </div>
                  {(devis.ventilation_tva || [{ taux: devis.tva, base_ht: devis.total_ht, montant_tva: devis.total_ttc - devis.total_ht, autoliquidation: false }]).map((v) => (
                    <div key={v.autoliquidation ? "AL" : v.taux} className="flex justify-between text-stone-400 text-sm mt-1"
                         data-testid={`public-tva-${v.autoliquidation ? "al" : String(v.taux).replace(".", "-")}`}>
                      <span>{v.autoliquidation ? t("public_devis.al_zero") : t("public_devis.tva_ligne", { taux: String(v.taux).replace(".", ","), base: eur(v.base_ht) })}</span>
                      <span className="font-serif">{eur(v.montant_tva)}</span>
                    </div>
                  ))}
                  {devis.autoliquidation && (
                    <p className="text-[#D9A461] text-xs mt-2 font-semibold" data-testid="mention-autoliquidation">
                      {t("public_devis.al_mention")}
                    </p>
                  )}
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-white/10">
                    <span className="text-white font-semibold">{t("public_devis.total_ttc")}</span>
                    <span className="font-serif font-bold text-2xl text-[#D9A461]">{eur(devis.total_ttc)}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                {accepted ? (
                  <>
                    <div className="bg-[#E4F2E7] border border-[#B8E2C2] rounded-xl p-5 flex items-center gap-3"
                         data-testid="public-devis-accepte">
                      <CheckCircle2 size={24} className="text-[#276440] shrink-0" />
                      <div>
                        <div className="font-bold text-[#276440]">{t("public_devis.accepte_titre")}</div>
                        <div className="text-sm text-[#276440]/80">{t("public_devis.accepte_texte")}</div>
                      </div>
                    </div>
                    {devis.signature?.data && (
                      <div className="bg-white border border-[#E6DFD1] rounded-xl p-4" data-testid="signature-enregistree">
                        <div className="font-semibold text-sm mb-2">{t("public_devis.signature_enregistree")}</div>
                        <img src={devis.signature.data} alt={t("public_devis.signature_enregistree")} className="h-20" />
                        <p className="text-xs text-stone-500 mt-1">
                          {t("public_devis.signe_par")}{devis.signature.nom && ` ${t("public_devis.par")} ${devis.signature.nom}`}
                          {devis.signature.date && ` ${t("public_devis.le")} ${dateTimeFr(devis.signature.date)}`}
                        </p>
                      </div>
                    )}
                  </>
                ) : refused ? (
                  <div className="bg-[#FBE4E1] border border-[#F5C2BC] rounded-xl p-5" data-testid="public-devis-refuse">
                    <div className="font-bold text-[#A32E20]">{t("public_devis.refuse_titre")}</div>
                    <div className="text-sm text-[#A32E20]/80">{t("public_devis.refuse_texte")}</div>
                  </div>
                ) : (
                  <>
                    <div className="bg-white border border-[#E6DFD1] rounded-xl p-4 space-y-3" data-testid="bloc-signature-public">
                      <div className="font-semibold text-sm flex items-center gap-2">
                        <PenLine size={16} className="text-[#8A5A2B]" /> {t("public_devis.signature_titre")}
                      </div>
                      <input className="input-field" placeholder={t("public_devis.signature_nom")} value={nom}
                             onChange={(e) => setNom(e.target.value)} data-testid="input-nom-signataire" />
                      <SignaturePad onSign={setSigData} effacerLabel={t("public_devis.effacer")} />
                      <p className="text-[11px] text-stone-500">{t("public_devis.signature_note")}</p>
                    </div>
                    <button onClick={accepter} disabled={busy} className="btn-accent w-full !h-14 !text-lg"
                            data-testid="btn-accepter-devis">
                      {busy ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle2 size={20} />}
                      {sigData ? t("public_devis.signer_accepter") : t("public_devis.accepter")}
                    </button>
                    <button onClick={() => setRefuseOpen(true)} disabled={busy}
                            className="w-full h-11 text-sm font-semibold text-stone-500 hover:text-[#A32E20] transition-colors"
                            data-testid="btn-refuser-devis">
                      {t("public_devis.refuser")}
                    </button>
                  </>
                )}
              </div>

              <p className="text-xs text-stone-500 mt-6">
                {devis.entreprise?.raison_sociale} — SIRET {devis.entreprise?.siret} — {devis.entreprise?.adresse}.{" "}
                {t("public_devis.validite")}
              </p>
            </div>
          </div>
        )}

        <Modal open={refuseOpen} onClose={() => setRefuseOpen(false)} title={t("public_devis.refuser_titre")} testid="modal-refus-devis">
          <p className="text-sm text-stone-600 mb-5">{t("public_devis.refuser_texte")}</p>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setRefuseOpen(false)} className="btn-secondary" data-testid="btn-annuler-refus">{t("commun.annuler")}</button>
            <button onClick={refuser} disabled={busy}
                    className="h-12 rounded-lg bg-[#A32E20] hover:bg-[#8A271B] text-white font-bold text-sm active:scale-[0.98] transition-transform flex items-center justify-center"
                    data-testid="btn-confirmer-refus">
              {busy ? <Loader2 className="animate-spin" size={18} /> : t("public_devis.confirmer_refus")}
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
}
